/* Copyright (c) 2024 MOCA Systems, Inc. All rights reserved. */

import { EsidDisambiguationTicketActions, IESIDData, ImportType } from './import-tickets';

export function isEmptyObject(object: Record<string, unknown> | undefined): boolean {
	return (
		object === undefined ||
		object === null ||
		(typeof object === 'object' && Object.keys(object).length === 0)
	);
}

/**
 * Parses ESID data based on the selected import type.
 *
 * @param importType - The import type selected.
 * @param esidData - The ESID data to be parsed.
 * @returns - The parsed ESID data.
 */
export function parseESIDData(importType: ImportType, esidData: IESIDData): IESIDData {
	if (importType === ImportType.CREATE_UNIQUE_ESIDS) {
		/**
		 * If the user selects "create only", any tickets with matching ESIDs should be disregarded,
		 * and only ESIDs without matching tickets should have the property createNew set to true.
		 */
		return Object.entries(esidData).reduce((parsedESIDdata, [esid, esidItem]) => {
			if (isEmptyObject(esidItem.tickets)) {
				parsedESIDdata[esid] = {
					externalScheduleId: esid,
					createNew: true,
				};
				// If it has tickets, mark them as "ignore" and also avoid creating a new one because we want to create unique ESIDs only.
			} else if (esidItem.tickets && !isEmptyObject(esidItem.tickets)) {
				parsedESIDdata[esid] = {
					externalScheduleId: esid,
					createNew: false,
					tickets: Object.keys(esidItem.tickets).reduce((parsedTickets, ticketId) => {
						parsedTickets[ticketId] = { action: EsidDisambiguationTicketActions.Ignore };
						return parsedTickets;
					}, {} as Record<string, { action: EsidDisambiguationTicketActions }>),
				};
			}
			return parsedESIDdata;
		}, {} as IESIDData);
	} else if (importType === ImportType.CREATE) {
		// If the user selects "create with duplicates", set all updates to be ignored and set createNew to true for all ESIDs.
		return Object.entries(esidData).reduce((parsedESIDdata, [esid, esidItem]) => {
			parsedESIDdata[esid] = {
				externalScheduleId: esid,
				createNew: true,
			};
			// If it has tickets associated with it, then we ignore them.
			if (esidItem.tickets && !isEmptyObject(esidItem.tickets)) {
				parsedESIDdata[esid].tickets = Object.keys(esidItem.tickets).reduce((parsedTickets, ticketId) => {
					parsedTickets[ticketId] = { action: EsidDisambiguationTicketActions.Ignore };
					return parsedTickets;
				}, {} as Record<string, { action: EsidDisambiguationTicketActions }>);
			}
			return parsedESIDdata;
		}, {} as IESIDData);
	} else if (importType === ImportType.UPDATE) {
		/**
		 * If the user selects "updates only", set createNew to explicitly false for all entries in esidData.
		 * Entries with a one-to-one mapping of esid/ticketIds can be preset for updates,
		 * while any quantity greater than that should remain unset.
		 */
		return Object.entries(esidData).reduce((parsedESIDdata, [esid, esidItem]) => {
			if (!!esidItem.tickets && !isEmptyObject(esidItem.tickets)) {
				parsedESIDdata[esid] = {
					externalScheduleId: esid,
					createNew: false,
					tickets: Object.keys(esidItem.tickets).reduce((parsedTickets, ticketId, index, esidItemTickets) => {
						if (esidItemTickets.length === 1) {
							parsedTickets[ticketId] = { action: EsidDisambiguationTicketActions.Update };
						} else {
							parsedTickets[ticketId] = { action: EsidDisambiguationTicketActions.Unset };
						}
						return parsedTickets;
					}, {} as Record<string, { action: EsidDisambiguationTicketActions }>),
				};
				// If it doesn't have tickets, avoid creating a new one since we are only updating existing ones.
			} else if (isEmptyObject(esidItem.tickets)) {
				parsedESIDdata[esid] = {
					externalScheduleId: esid,
					createNew: false,
				};
			}
			return parsedESIDdata;
		}, {} as IESIDData);
	} else if (importType === ImportType.CREATE_AND_UPDATE) {
		/**
		 * If the user selects "create + update":
		 * - Set createNew to true for anything without esid matches.
		 * - Set createNew to false for anything with a single esid match to update.
		 * - Leave anything with multiple esid matches as unset, to be filled in by the user.
		 */
		return Object.entries(esidData).reduce((parsedESIDdata, [esid, esidItem]) => {
			// it does not have maching tickets, so create.
			if (isEmptyObject(esidItem.tickets)) {
				parsedESIDdata[esid] = {
					externalScheduleId: esid,
					createNew: true,
				};
				// it does have maching tickets, so update.
			} else if (!!esidItem.tickets && !isEmptyObject(esidItem.tickets)) {
				parsedESIDdata[esid] = {
					externalScheduleId: esid,
					createNew: false,
					tickets: Object.keys(esidItem.tickets).reduce((parsedTickets, ticketId, index, esidItemTickets) => {
						if (esidItemTickets.length === 1) {
							parsedTickets[ticketId] = { action: EsidDisambiguationTicketActions.Update };
						} else {
							parsedTickets[ticketId] = { action: EsidDisambiguationTicketActions.Unset };
						}
						return parsedTickets;
					}, {} as Record<string, { action: EsidDisambiguationTicketActions }>),
				};
			}
			return parsedESIDdata;
		}, {} as IESIDData);
	} else {
		throw new Error('Unsupported import type.');
	}
}

/**
 * Splits the provided ESID data into two separate objects based on the presence of "Unset" tickets.
 * @param data - The ESID data to be split.
 * @returns An object containing two properties: "withUnset" and "withoutUnset".
 *          - "withUnset" contains the ESID data that has "Unset" tickets.
 *          - "withoutUnset" contains the ESID data that does not have "Unset" tickets.
 */
export function splitEsidData(data: IESIDData): { withUnset: IESIDData; withoutUnset: IESIDData } {
	const withUnset: IESIDData = {};
	const withoutUnset: IESIDData = {};

	for (const [key, value] of Object.entries(data)) {
		const hasUnset = Object.values(value.tickets ?? {}).some(
			(ticket) => ticket.action === EsidDisambiguationTicketActions.Unset
		);
		if (hasUnset) {
			withUnset[key] = value;
		} else {
			withoutUnset[key] = value;
		}
	}

	return { withUnset, withoutUnset };
}

/**
 * Joins two ESID data objects together.
 * @param dataA - The first ESID data object.
 * @param dataB - The second ESID data object.
 * @returns The joined ESID data object.
 */
export function joinEsidData(dataA: IESIDData | undefined, dataB: IESIDData | undefined): IESIDData {
	return { ...(dataA || {}), ...(dataB || {}) };
}
